import { Link } from "react-router-dom";
import { SITE, usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import { ArrowLeft, Compass, Heart, ArrowRight } from "lucide-react";
import { Footer } from "@/components/Footer";
import postsData from "@/data/posts.json";

export const blockText = (b) => (typeof b === "string" ? b : b.h || `${b.b} : ${b.t}`);

export const C = {
  bg: "#1C1C1E",
  title: "#F4EAD4",
  body: "#E5E5EA",
  mist: "#A2A2A6",
  goldMat: "#C5A059",
  goldLine: "#E6C687",
};

export const POSTS = postsData;

const blogJsonLd = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": `${SITE}/observatoire#blog`,
  name: "L'Observatoire des Possibles",
  description: "Le blog de La Petite Boîte Intuitive : Tarot de Marseille, intuition, libre arbitre et questions du cœur.",
  url: `${SITE}/observatoire`,
  inLanguage: "fr-FR",
  publisher: { "@type": "Organization", name: "La Petite Boîte Intuitive", url: `${SITE}/` },
  blogPost: POSTS.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    description: p.excerpt,
    url: `${SITE}/observatoire/${p.slug}`,
    inLanguage: "fr-FR",
    datePublished: p.dateISO,
    ...(p.image ? { image: p.image } : {}),
  })),
};

export const PostImage = ({ post, testid }) => {
  if (!post.image) return null;
  return post.imageCard ? (
    <div className="flex justify-center mb-8">
      <img
        src={post.image}
        alt={post.title}
        data-testid={testid}
        className="max-h-[440px] w-auto rounded-xl"
        style={{ border: "1px solid rgba(230,198,135,0.18)", boxShadow: "0 10px 44px rgba(0,0,0,0.55)" }}
        loading="lazy"
      />
    </div>
  ) : (
    <img
      src={post.image}
      alt={post.title}
      data-testid={testid}
      className="w-full h-52 sm:h-72 object-cover rounded-xl mb-7"
      style={{ objectPosition: "center 42%", border: "1px solid rgba(230,198,135,0.15)" }}
      loading="lazy"
    />
  );
};

const PostCard = ({ post, i }) => (
  <motion.article
    data-testid={`blog-post-${i}`}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.08 }}
    className="group rounded-2xl border p-7 sm:p-8 transition-colors hover:border-[rgba(230,198,135,0.4)]"
    style={{ borderColor: "rgba(230,198,135,0.18)", backgroundColor: "rgba(255,255,255,0.025)" }}
  >
    <Link to={`/observatoire/${post.slug}`} data-testid={`blog-post-link-${i}`} className="block">
      {post.motif === "heart" && (
        <div className="flex items-center justify-center gap-3 mb-7" data-testid={`blog-post-motif-${i}`}>
          <span className="h-px w-12" style={{ background: "linear-gradient(to right, transparent, rgba(230,198,135,0.6))" }} />
          <Heart size={22} strokeWidth={1.3} style={{ color: C.goldLine }} />
          <span className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, rgba(230,198,135,0.6))" }} />
        </div>
      )}
      <PostImage post={post} testid={`blog-post-image-${i}`} />
      {post.date && (
        <p className="font-body uppercase tracking-[0.14em] text-[11px] mb-2" style={{ color: C.goldLine }}>{post.date}</p>
      )}
      <h2 className="font-display text-2xl mb-3 transition-colors group-hover:text-[#E6C687]" style={{ color: C.title }}>{post.title}</h2>
      {post.excerpt && (
        <p className="font-body text-sm leading-relaxed mb-5" style={{ color: C.mist }}>{post.excerpt}</p>
      )}
      <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.16em]" style={{ color: C.goldMat }}>
        Lire l'article
        <ArrowRight size={14} strokeWidth={1.6} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  </motion.article>
);

export default function Blog() {
  usePageMeta({
    title: "L'Observatoire des Possibles · Blog Tarot de Marseille | La Petite Boîte Intuitive",
    description: "Le blog de La Petite Boîte Intuitive : Tarot de Marseille, Lames du Mois, intuition et questions du cœur, par Lucie.",
    path: "/observatoire",
  });

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: C.bg }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }} />
      <div className="oracle-bg" />
      <div className="relative z-10 flex-1 max-w-3xl w-full mx-auto px-6 py-14 sm:py-20">
        <Link
          to="/"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 font-body text-xs tracking-[0.12em] uppercase mb-12 transition-colors hover:text-[#E6C687]"
          style={{ color: C.mist }}
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          Retour à l'accueil
        </Link>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="flex justify-center mb-5">
            <Compass size={30} strokeWidth={1.3} style={{ color: C.goldLine }} />
          </div>
          <p className="font-body uppercase tracking-[0.35em] text-xs" style={{ color: C.mist }}>Le blog</p>
          <h1 className="font-display uppercase tracking-[0.06em] text-4xl sm:text-5xl font-light mt-4" style={{ color: C.title }}>
            L'Observatoire des Possibles
          </h1>
          <span className="block mx-auto my-7 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
          <p className="font-body text-base leading-relaxed max-w-xl mx-auto" style={{ color: C.body }}>
            Un espace pour explorer l'invisible, écouter ton intuition et ouvrir les portes des possibles.
          </p>
        </motion.div>

        <div data-testid="blog-posts" className="mt-14 space-y-6">
          {POSTS.map((post, i) => <PostCard key={post.slug} post={post} i={i} />)}
        </div>
      </div>
      <Footer />
    </div>
  );
}
